// 帳號 VMS 證據匯出（MV.5，docs/mycam/mv-spec.md ④）：瀏覽器組包、雲端只簽章。
//
//   1. 每台的合併時間軸（雲端 → 本機）切成 ≤ 5 分鐘的片段（記憶體不爆）；
//   2. 逐片取影片：雲端 = 片段 TS 接起來、本機（SD / 手機）= 回放隧道的 /pb.m3u8 窗；都 remux 成 MP4（不轉碼）；
//   3. 每個檔 SHA-256 → manifest.json（含 events.json 的雜湊）；
//   4. POST /v3/me/exports/sign（manifest 原樣位元組 + 重新輸入的密碼）→ ES256 簽章；
//   5. ZIP：manifest.json + manifest.sig + 各 MP4 + events.json。
// 驗證（/vms/verify，不用登入）：解 ZIP → 逐檔重算 SHA-256 對 manifest → 用 GET /v3/exports/keys 的公鑰驗 manifest 簽章。

import { apiFetch, tokenStore } from '../api';
import { mediaFetchUrl, type CloudClip } from '../recPlayback/cloudM3u8';
import { fetchSdWindowTs } from '../recPlayback/sdClips';
import { transmuxTsToMp4, withTimeout } from '../recPlayback/tsToMp4';
import { openRemotePlayback, type RemotePlaybackSession } from '../recPlayback/webrtcPlayback';
import type { Span } from './timeline';
import { buildZip, readZip, type ZipEntry } from './zip';

export const EXPORT_MAX_CAMS = 4;
export const EXPORT_MAX_US = 30 * 60_000_000;
export const EXPORT_PIECE_US = 5 * 60_000_000;
/** 一片最多等多久（隧道卡住 / 片段正在被覆蓋時不讓整包一直等）；逾時 = 這片取不到，隧道重開。 */
export const EXPORT_PIECE_TIMEOUT_MS = 180_000;
const REOPEN_SETTLE_MS = 3_000;
const REOPEN_RETRY_MS = 5_000;

export type EvidenceSource = 'cloud' | 'sdcard' | 'phone';

export interface Piece {
  deviceId: string;
  source: 'cloud' | 'local';
  fromUs: number;
  toUs: number;
}

/** 有錄影的區段 ∩ [from, to)，再切成 ≤ pieceUs 的片段。 */
export function planPieces(deviceId: string, spans: Span[], fromUs: number, toUs: number, pieceUs = EXPORT_PIECE_US): Piece[] {
  const out: Piece[] = [];
  for (const s of spans) {
    if (s.kind !== 'recorded' || !s.source) continue;
    const a = Math.max(s.fromUs, fromUs);
    const b = Math.min(s.toUs, toUs);
    for (let t = a; t < b; t += pieceUs) out.push({ deviceId, source: s.source, fromUs: t, toUs: Math.min(b, t + pieceUs) });
  }
  return out;
}

export interface ManifestFile {
  name: string;
  device_id: string;
  device_name: string;
  from_us: number;
  to_us: number;
  source: EvidenceSource;
  sha256: string;
  size: number;
  codec: string;
}

export interface Manifest {
  version: 1;
  export_id: string;
  exported_by: string;
  account: string;
  reason: string;
  created_at_us: number;
  window: { from_us: number; to_us: number };
  time_zone: string;
  files: ManifestFile[];
  events_file: { name: string; sha256: string; count: number };
}

export interface ManifestSig {
  alg: string;
  key_id: string;
  signature: string;
  manifest_sha256: string;
}

export async function sha256Hex(data: Blob | Uint8Array): Promise<string> {
  const buf = data instanceof Blob ? await data.arrayBuffer() : data;
  const d = await crypto.subtle.digest('SHA-256', buf as BufferSource);
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 登入權杖的 sub（伺服器比對 exported_by 用的就是它）。 */
export function currentUserId(): string | null {
  const tok = tokenStore.get();
  const part = tok?.split('.')[1];
  if (!part) return null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.sub === 'string' ? json.sub : null;
  } catch {
    return null;
  }
}

/** a***@example.com */
export function maskEmail(email?: string | null): string {
  if (!email || !email.includes('@')) return '';
  const [u, d] = email.split('@');
  return `${u.slice(0, 1)}***@${d}`;
}

export function newExportId(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const rnd = Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, '0')).join('');
  return `exp-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}-${rnd}`;
}

export function pieceFileName(deviceId: string, source: EvidenceSource, fromUs: number): string {
  const d = new Date(fromUs / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const id = deviceId.replace(/[^A-Za-z0-9._-]/g, '_');
  return `${id}_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}_${source}.mp4`;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** 時段內全部雲端片段（/recordings 不吃游標 → 用「從上一批最後一段之後」接著查；最多 40 批）。 */
export async function listCloudClips(deviceId: string, fromUs: number, toUs: number, page = 200): Promise<CloudClip[]> {
  const out: CloudClip[] = [];
  let f = fromUs;
  for (let i = 0; i < 40 && f < toUs; i++) {
    const res = await apiFetch<CloudClip[]>(`/v3/devices/${encodeURIComponent(deviceId)}/recordings`, { query: { from: f, to: toUs, limit: page } });
    const batch = (res.data ?? []).filter((c) => c.clip_start_ts >= f).sort((a, b) => a.clip_start_ts - b.clip_start_ts);
    out.push(...batch);
    if (batch.length < page) break;
    f = batch[batch.length - 1].clip_start_ts + 1;
  }
  return out;
}

/** 一片雲端：起點落在 [from, to) 的片段 TS 接起來 → MP4；沒有 → null。回傳實際涵蓋的時間。 */
async function fetchCloudPiece(p: Piece): Promise<{ blob: Blob; fromUs: number; toUs: number } | null> {
  const clips = (await listCloudClips(p.deviceId, p.fromUs, p.toUs))
    .filter((c) => c.url && c.clip_start_ts >= p.fromUs && c.clip_start_ts < p.toUs && c.clip_end_ts > c.clip_start_ts)
    .sort((a, b) => a.clip_start_ts - b.clip_start_ts);
  if (!clips.length) return null;
  const parts: Uint8Array[] = [];
  for (const c of clips) {
    const r = await fetch(mediaFetchUrl(c.url));
    if (!r.ok) throw new Error(`clip ${c.clip_id ?? ''} ${r.status}`);
    parts.push(new Uint8Array(await r.arrayBuffer()));
  }
  return { blob: await transmuxTsToMp4(concat(parts)), fromUs: clips[0].clip_start_ts, toUs: clips[clips.length - 1].clip_end_ts };
}

/** 一片本機：回放隧道取 [from, to) 的窗 → MP4；被覆蓋 / 沒有 → null。 */
async function fetchLocalPiece(p: Piece, session: RemotePlaybackSession): Promise<{ blob: Blob; fromUs: number; toUs: number } | null> {
  const { ts, written } = await fetchSdWindowTs((url) => session.tunnel.requestUrl(url), p.fromUs, p.toUs);
  if (written === 0) return null;
  return { blob: await transmuxTsToMp4(ts), fromUs: p.fromUs, toUs: p.toUs };
}

export interface ExportCam {
  deviceId: string;
  name: string;
  phone: boolean;
  spans: Span[];
}

export interface ExportProgress {
  done: number;
  total: number;
  current?: string;
  skipped: number;
  failed: number;
}

export interface BuiltFiles {
  files: { meta: Omit<ManifestFile, 'sha256' | 'size'>; blob: Blob }[];
  /** 已被覆蓋 / 沒有片段 */
  skipped: number;
  /** 逾時或出錯 */
  failed: number;
}

/** 取影片 + 轉 MP4（不簽、不打包）。每台本機只開一條回放隧道，做完關掉。 */
export async function collectFiles(
  cams: ExportCam[],
  fromUs: number,
  toUs: number,
  onProgress: (p: ExportProgress) => void,
  pieceTimeoutMs = EXPORT_PIECE_TIMEOUT_MS
): Promise<BuiltFiles> {
  const pieces = cams.flatMap((c) => planPieces(c.deviceId, c.spans, fromUs, toUs).map((p) => ({ p, cam: c })));
  const files: BuiltFiles['files'] = [];
  const sessions = new Map<string, RemotePlaybackSession>();
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const drop = async (id: string, settleMs = REOPEN_SETTLE_MS) => {
    const s = sessions.get(id);
    sessions.delete(id);
    if (!s) return;
    try {
      s.stop();
    } catch {
      /* noop */
    }
    await sleep(settleMs); // 等裝置放掉舊的回放連線再開新的（馬上重開會撞到還沒釋放的那條）
  };
  /** 這台的回放隧道（沒有就開；開失敗等一下再試一次）。 */
  const session = async (id: string): Promise<RemotePlaybackSession> => {
    const cur = sessions.get(id);
    if (cur) return cur;
    let s: RemotePlaybackSession;
    try {
      s = await withTimeout(openRemotePlayback(id), 30_000);
    } catch {
      await sleep(REOPEN_RETRY_MS);
      s = await withTimeout(openRemotePlayback(id), 30_000);
    }
    sessions.set(id, s);
    return s;
  };
  let skipped = 0;
  let failed = 0;
  try {
    for (let i = 0; i < pieces.length; i++) {
      const { p, cam } = pieces[i];
      onProgress({ done: i, total: pieces.length, current: cam.name, skipped, failed });
      let got: { blob: Blob; fromUs: number; toUs: number } | null = null;
      let ok = false;
      // 同一片最多試兩次。隧道偶爾會卡住（某段 30 s 沒回；實測同一段單獨取又正常）→ 卡住的隧道就不能再用：
      // 丟掉、等裝置放掉、開新的再試；兩次都失敗才算取不到。
      for (let attempt = 0; attempt < 2 && !ok; attempt++) {
        try {
          if (p.source === 'cloud') got = await withTimeout(fetchCloudPiece(p), pieceTimeoutMs);
          else got = await withTimeout(fetchLocalPiece(p, await session(p.deviceId)), pieceTimeoutMs);
          ok = true;
        } catch (e) {
          console.warn('[export] piece failed', attempt, p.deviceId, p.fromUs, String(e).slice(0, 300));
          if (p.source === 'local') await drop(p.deviceId);
        }
      }
      if (!ok) {
        failed++;
        continue;
      }
      if (!got) {
        skipped++;
        continue;
      }
      const source: EvidenceSource = p.source === 'cloud' ? 'cloud' : cam.phone ? 'phone' : 'sdcard';
      files.push({
        meta: { name: pieceFileName(p.deviceId, source, got.fromUs), device_id: p.deviceId, device_name: cam.name, from_us: got.fromUs, to_us: got.toUs, source, codec: 'h264' },
        blob: got.blob,
      });
    }
    onProgress({ done: pieces.length, total: pieces.length, skipped, failed });
  } finally {
    for (const id of [...sessions.keys()]) await drop(id, 0);
  }
  return { files, skipped, failed };
}

export async function signManifest(bytes: Uint8Array, password: string): Promise<ManifestSig> {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  const res = await apiFetch<ManifestSig>('/v3/me/exports/sign', { method: 'POST', body: { manifest: btoa(bin), password } });
  if (!res.data) throw new Error('no signature');
  return res.data;
}

export interface PackInput {
  files: BuiltFiles['files'];
  eventsJson: string;
  eventsCount: number;
  exportId: string;
  userId: string;
  account: string;
  reason: string;
  window: { from_us: number; to_us: number };
  timeZone: string;
  now?: Date;
}

/** 算雜湊、組 manifest（位元組）。 */
export async function buildManifest(inp: PackInput): Promise<{ manifest: Manifest; bytes: Uint8Array }> {
  const files: ManifestFile[] = [];
  for (const f of inp.files) files.push({ ...f.meta, sha256: await sha256Hex(f.blob), size: f.blob.size });
  const manifest: Manifest = {
    version: 1,
    export_id: inp.exportId,
    exported_by: inp.userId,
    account: inp.account,
    reason: inp.reason,
    created_at_us: (inp.now ?? new Date()).getTime() * 1000,
    window: inp.window,
    time_zone: inp.timeZone,
    files,
    events_file: { name: 'events.json', sha256: await sha256Hex(new TextEncoder().encode(inp.eventsJson)), count: inp.eventsCount },
  };
  return { manifest, bytes: new TextEncoder().encode(JSON.stringify(manifest, null, 2)) };
}

export async function packZip(manifestBytes: Uint8Array, sig: ManifestSig, files: BuiltFiles['files'], manifest: Manifest, eventsJson: string): Promise<Blob> {
  const entries: ZipEntry[] = [
    { name: 'manifest.json', data: new Blob([manifestBytes as BlobPart]) },
    { name: 'manifest.sig', data: new Blob([JSON.stringify(sig, null, 2)]) },
    { name: 'events.json', data: new Blob([eventsJson]) },
    ...files.map((f, i) => ({ name: manifest.files[i].name, data: f.blob })),
  ];
  return buildZip(entries);
}

// ---- 驗證 ----

export interface VerifyFileResult {
  name: string;
  ok: boolean;
  reason?: 'missing' | 'hash' | 'extra';
}

export interface VerifyResult {
  signatureOk: boolean;
  keyFound: boolean;
  manifest: Manifest | null;
  files: VerifyFileResult[];
  ok: boolean;
  error?: string;
}

type Jwk = JsonWebKey & { kid?: string };

export async function fetchExportKeys(): Promise<Jwk[]> {
  const r = await fetch('/v3/exports/keys');
  if (!r.ok) throw new Error(`keys ${r.status}`);
  const j = await r.json();
  return (j?.data?.keys ?? []) as Jwk[];
}

export async function verifySignature(manifestBytes: Uint8Array, sig: ManifestSig, keys: Jwk[]): Promise<{ ok: boolean; keyFound: boolean }> {
  const jwk = keys.find((k) => k.kid === sig.key_id);
  if (!jwk) return { ok: false, keyFound: false };
  const key = await crypto.subtle.importKey('jwk', { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y, ext: true }, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  const raw = Uint8Array.from(atob(sig.signature), (c) => c.charCodeAt(0));
  const ok = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, raw, manifestBytes as BufferSource);
  return { ok, keyFound: true };
}

/** 驗一包匯出：簽章 + 每個檔的雜湊（多出 / 少了的檔也列出）。 */
export async function verifyExport(zip: Blob, keys?: Jwk[]): Promise<VerifyResult> {
  const fail = (error: string): VerifyResult => ({ signatureOk: false, keyFound: false, manifest: null, files: [], ok: false, error });
  let entries;
  try {
    entries = await readZip(zip);
  } catch {
    return fail('notZip');
  }
  const byName = new Map(entries.map((e) => [e.name, e]));
  const mEntry = byName.get('manifest.json');
  const sEntry = byName.get('manifest.sig');
  if (!mEntry || !sEntry) return fail('noManifest');
  const manifestBytes = new Uint8Array(await mEntry.data.arrayBuffer());
  let manifest: Manifest;
  let sig: ManifestSig;
  try {
    manifest = JSON.parse(new TextDecoder().decode(manifestBytes));
    sig = JSON.parse(await sEntry.data.text());
  } catch {
    return fail('badManifest');
  }
  const { ok: signatureOk, keyFound } = await verifySignature(manifestBytes, sig, keys ?? (await fetchExportKeys()));
  const files: VerifyFileResult[] = [];
  const listed = new Set<string>(['manifest.json', 'manifest.sig']);
  const check = async (name: string, sha: string) => {
    listed.add(name);
    const e = byName.get(name);
    if (!e) files.push({ name, ok: false, reason: 'missing' });
    else files.push((await sha256Hex(e.data)) === sha ? { name, ok: true } : { name, ok: false, reason: 'hash' });
  };
  for (const f of manifest.files ?? []) await check(f.name, f.sha256);
  if (manifest.events_file) await check(manifest.events_file.name, manifest.events_file.sha256);
  for (const e of entries) if (!listed.has(e.name)) files.push({ name: e.name, ok: false, reason: 'extra' });
  return { signatureOk, keyFound, manifest, files, ok: signatureOk && files.every((f) => f.ok) };
}

